'use client';
import { Dialog, Portal, type DialogRootProps } from '@ark-ui/react';
import { useCallback } from 'react';
import { memo } from 'react';
import { Button } from '@app/_components/button';
import { Paragraph } from '@styled-system/jsx';
import { styled } from '@styled-system/jsx';

export type ModalProps = {
  isOpen: boolean;
  onClickRegister: () => void;
  onClose?: () => void;
} & DialogRootProps;

const _VipOnlyModal = ({ isOpen, onClickRegister, onClose }: ModalProps) => {
  const handleOnOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );
  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      closeOnEscape={false}
      closeOnInteractOutside={false}
      open={isOpen}
      onOpenChange={handleOnOpenChange}
    >
      {/* NOTE: TSのバージョンを上げたら直す */}
      <Portal>
        <Backdrop />
        <Positioner>
          <Content>
            <Paragraph
              mt="40px"
              lineHeight="24px"
              fontSize="size_20"
              fontWeight="700"
              mb="40px"
            >
              VIP限定作品です
            </Paragraph>
            <Button
              size="small"
              w="100%"
              maxW="360px"
              height="40px"
              onClick={onClickRegister}
              backgroundColor="gold"
              color="white"
              fontSize="size_16"
              fontWeight="bold"
              margin="8px auto 8px auto"
              _focus={{
                outline: 0,
              }}
            >
              無料トライアルをはじめる
            </Button>
            <Paragraph color="red" fontSize="size_12" fontWeight="bold">
              今なら7日間無料トライアル実施中！
            </Paragraph>
            <Paragraph fontSize="size_12" color="accent.1000">
              7日後¥980/1ヶ月
            </Paragraph>
            <Button
              size="small"
              w="100%"
              maxW="360px"
              height="40px"
              onClick={onClose}
              backgroundColor="accent.700"
              color="white"
              fontSize="size_16"
              fontWeight="bold"
              margin="32px auto 32px auto"
              _focus={{
                outline: 0,
              }}
            >
              キャンセル
            </Button>
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(20px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'modal_backdrop',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: '300px',
    },
    width: {
      base: '100dvw',
      lg: '580px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    textAlign: 'center',
    px: '4',
    borderRadius: {
      base: '8px',
    },
    height: 'auto',
    width: '90%',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

export const VipOnlyModal = memo(_VipOnlyModal);
